import {
    AlertDialog,
    AlertDialogBody,
    AlertDialogCloseButton,
    AlertDialogContent,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogOverlay,
    Box,
    Button,
    Checkbox,
    HStack,
    Text,
    Icon,
    Flex,
    useToast,
    Td,
    Tr,
    InputGroup,
    InputLeftElement,
    Input,
} from '@chakra-ui/react';
import { useState, useMemo, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import SortArrowIcon, { SortIconDirection } from '../SortArrowIcon';
import { TbSearch, TbTrash, TbPercentage } from 'react-icons/tb';
import { OrgVendor } from '../../hooks/useGetSettings';
import { useGetLocations } from '../../hooks/useGetLocations';
import { useUpdateSettings } from '../../hooks/useUpdateSettings';
import { RefetchOptions, QueryObserverResult } from '@tanstack/react-query';
import PartBayButton from '../PartBayButton';
import Pagination from '../Pagination';
import BulkActionBar, { BulkActionBarItem } from '../BulkActionBar';
import {
    applyVendorPreference,
    InlineVendorPreferenceCell,
    makesLabel,
    makesTitle,
    PREFERENCE_FIELDS,
    type VendorLocations,
    type VendorPreferenceField,
} from './VendorsInlineCells';
import VendorsBulkDiscountModal from './VendorsBulkDiscountModal';
import {
    SettingsEmptyState,
    SettingsInlineNumberEdit,
    SettingsPageActions,
    SettingsRowActions,
    SettingsTable,
    SettingsTableFooter,
    SettingsTd,
    SettingsTh,
    useDynamicPageSize,
} from './primitives';
import { PARTBAY_BLUE, SETTINGS_TABLE_GUTTER } from './primitives/constants';

interface VendorsInterface {
    vendors: Array<OrgVendor>;
    refetch: (options?: RefetchOptions) => Promise<QueryObserverResult<any, Error>>;
    isReadOnly?: boolean;
}

/** The five preference columns, matching PREFERENCE_FIELDS order. */
const PREFERENCE_COLUMNS: Array<{ key: VendorPreferenceField; label: string }> = [
    { key: PREFERENCE_FIELDS[0], label: 'REQUIRED' },
    { key: PREFERENCE_FIELDS[1], label: 'SUPER PREF.' },
    { key: PREFERENCE_FIELDS[2], label: 'PREFERRED' },
    { key: PREFERENCE_FIELDS[3], label: 'AVOID' },
    { key: PREFERENCE_FIELDS[4], label: 'PROHIBITED' },
];

/** Removing and re-pricing are the only changes that make sense in bulk. */
const BULK_ACTIONS: BulkActionBarItem[] = [
    { id: 'discount', label: 'Discount', icon: TbPercentage as React.ElementType },
    { id: 'remove', label: 'Remove', icon: TbTrash as React.ElementType, danger: true },
];

const Vendors = ({ vendors, refetch, isReadOnly = false }: VendorsInterface) => {
    const [localVendors, setLocalVendors] = useState<OrgVendor[]>(vendors);
    const [searchTerm, setSearchTerm] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [nameSortDirection, setNameSortDirection] = useState<'asc' | 'desc' | null>(null);
    // Rows are identified by their position in the vendors array: one supplier
    // can hold several rows (the same vendor with different makes), so the
    // supplier id is not unique.
    const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
    const [isBulkDiscountOpen, setIsBulkDiscountOpen] = useState(false);
    const [isBulkRemoveOpen, setIsBulkRemoveOpen] = useState(false);
    const [isBulkSaving, setIsBulkSaving] = useState(false);
    const bulkRemoveCancelRef = useRef<HTMLButtonElement>(null);
    const navigate = useNavigate();
    const { scrollRef: tableScrollRef, pageSize: vendorsPerPage } = useDynamicPageSize();

    const updateSettingsMutation = useUpdateSettings();
    const { data: locationsData } = useGetLocations();
    const locations = locationsData?.locations ?? [];
    const toast = useToast();

    useEffect(() => {
        setLocalVendors(vendors);
    }, [vendors]);

    // Rows carry the index they hold in localVendors so searching, sorting and
    // paging never lose the identity every save path writes back through.
    const filteredVendors = useMemo(() => {
        let result = localVendors.map((vendor, index) => ({ vendor, index }));

        if (searchTerm) {
            result = result.filter(({ vendor }) =>
                vendor.name.toLowerCase().includes(searchTerm.toLowerCase())
            );
        }

        if (nameSortDirection) {
            result = [...result].sort((a, b) => {
                const comparison = a.vendor.name.localeCompare(b.vendor.name);
                return nameSortDirection === 'asc' ? comparison : -comparison;
            });
        }

        return result;
    }, [localVendors, searchTerm, nameSortDirection]);

    const totalPages = useMemo(() => Math.ceil(filteredVendors.length / vendorsPerPage), [filteredVendors.length, vendorsPerPage]);

    // The page size follows the viewport, so a resize can shrink the page
    // count; clamp so the user is never stranded on a now-empty page.
    useEffect(() => {
        setCurrentPage((prev) => Math.min(prev, Math.max(1, totalPages)));
    }, [totalPages]);

    const currentVendors = useMemo(() => {
        const startIndex = (currentPage - 1) * vendorsPerPage;
        const endIndex = startIndex + vendorsPerPage;
        return filteredVendors.slice(startIndex, endIndex);
    }, [filteredVendors, currentPage, vendorsPerPage]);

    // Search can hide a selected row; drop those ids so the bulk bar never acts
    // on vendors who are no longer on screen.
    useEffect(() => {
        setSelectedIds((prev) => {
            if (prev.size === 0) return prev;
            const visible = new Set(filteredVendors.map(({ index }) => index));
            const next = new Set(Array.from(prev).filter((id) => visible.has(id)));
            return next.size === prev.size ? prev : next;
        });
    }, [filteredVendors]);

    const allPageSelected =
        currentVendors.length > 0 &&
        currentVendors.every(({ index }) => selectedIds.has(index));
    const somePageSelected = currentVendors.some(({ index }) => selectedIds.has(index));

    const selectedVendors = useMemo(
        () => localVendors.filter((_, index) => selectedIds.has(index)),
        [localVendors, selectedIds],
    );

    const handleToggleSelect = (index: number, checked: boolean) => {
        setSelectedIds((prev) => {
            const next = new Set(prev);
            if (checked) next.add(index);
            else next.delete(index);
            return next;
        });
    };

    // Header checkbox covers the current page only, matching what is visible.
    const handleSelectAllOnPage = (checked: boolean) => {
        setSelectedIds((prev) => {
            const next = new Set(prev);
            currentVendors.forEach(({ index }) => {
                if (checked) next.add(index);
                else next.delete(index);
            });
            return next;
        });
    };

    const handleSearchChange = (value: string) => {
        setSearchTerm(value);
        setCurrentPage(1);
    };

    const handleNameSortToggle = () => {
        setNameSortDirection(prev => {
            if (prev === null) return 'asc';
            if (prev === 'asc') return 'desc';
            return null;
        });
        setCurrentPage(1);
    };

    const getNameSortDirection = (): SortIconDirection => {
        if (nameSortDirection === 'asc') return 'asc';
        if (nameSortDirection === 'desc') return 'desc';
        return 'none';
    };

    // Add and edit both land on the full-screen vendor page.
    const handleOpenAdd = () => navigate('/settings/vendors/new');
    const handleOpenEdit = (index: number) => navigate(`/settings/vendors/${index}`);

    /**
     * POST /settings/update replaces the whole vendors array, so every edit —
     * inline, bulk, or delete — sends the full list with just its rows changed.
     */
    const saveVendors = (
        updatedVendors: OrgVendor[],
        copy: { title: string; description?: string; errorTitle: string },
        options?: { onSettled?: () => void },
    ) => {
        updateSettingsMutation.mutate(
            { vendors: updatedVendors },
            {
                onSuccess: () => {
                    setLocalVendors(updatedVendors);
                    toast({
                        title: copy.title,
                        description: copy.description,
                        status: 'success',
                        duration: 3000,
                        isClosable: true,
                    });
                    options?.onSettled?.();
                },
                onError: (error) => {
                    toast({
                        title: copy.errorTitle,
                        description: error.message || 'Please try again.',
                        status: 'error',
                        duration: 5000,
                        isClosable: true,
                    });
                    options?.onSettled?.();
                },
            },
        );
    };

    const patchVendor = (index: number, changes: Partial<OrgVendor>): OrgVendor[] =>
        localVendors.map((v, i) => (i === index ? { ...v, ...changes } : v));

    const handleInlineDiscountSave = (
        vendor: OrgVendor,
        index: number,
        percent: number | null,
    ) => {
        saveVendors(
            patchVendor(index, {
                discount: percent === null ? undefined : percent / 100,
            }),
            {
                title: 'Discount updated',
                description:
                    percent === null
                        ? `Discount cleared for ${vendor.name}`
                        : `${vendor.name} discount set to ${percent}%`,
                errorTitle: 'Error updating discount',
            },
        );
    };

    const handlePreferenceSave = (
        vendor: OrgVendor,
        index: number,
        key: VendorPreferenceField,
        label: string,
        next: VendorLocations,
    ) => {
        saveVendors(patchVendor(index, applyVendorPreference(vendor, key, next, locations)), {
            title: 'Preference updated',
            description: `${vendor.name} ${label.toLowerCase()} locations saved`,
            errorTitle: 'Error updating preference',
        });
    };

    const handleDeleteVendor = (vendor: OrgVendor, index: number) => {
        saveVendors(
            localVendors.filter((_, i) => i !== index),
            {
                title: 'Vendor removed',
                description: `${vendor.name} has been removed`,
                errorTitle: 'Error removing vendor',
            },
        );
    };

    const handleBulkDiscountApply = (percent: number | null) => {
        const changedIndexes = new Set(
            localVendors.flatMap((vendor, index) => {
                if (!selectedIds.has(index)) return [];
                const current =
                    vendor.discount == null ? null : Math.round(vendor.discount * 10000) / 100;
                return current === percent ? [] : [index];
            }),
        );
        const changedCount = changedIndexes.size;

        if (changedCount === 0) {
            toast({
                title: 'No changes to apply',
                description: 'The selected vendors already have that discount.',
                status: 'info',
                duration: 4000,
                isClosable: true,
            });
            setIsBulkDiscountOpen(false);
            return;
        }

        const updatedVendors = localVendors.map((vendor, index) =>
            changedIndexes.has(index)
                ? {
                      ...vendor,
                      discount: percent === null ? undefined : percent / 100,
                  }
                : vendor,
        );

        setIsBulkSaving(true);
        saveVendors(
            updatedVendors,
            {
                title: `${changedCount} vendor${changedCount === 1 ? '' : 's'} updated`,
                description:
                    percent === null
                        ? 'Discounts cleared'
                        : `Discount set to ${percent}%`,
                errorTitle: 'Error updating discounts',
            },
            {
                onSettled: () => {
                    setIsBulkSaving(false);
                    setIsBulkDiscountOpen(false);
                },
            },
        );
    };

    const handleBulkRemove = () => {
        const removedCount = selectedVendors.length;
        setIsBulkSaving(true);
        saveVendors(
            localVendors.filter((_, index) => !selectedIds.has(index)),
            {
                title: `${removedCount} vendor${removedCount === 1 ? '' : 's'} removed`,
                errorTitle: 'Error removing vendors',
            },
            {
                onSettled: () => {
                    setIsBulkSaving(false);
                    setIsBulkRemoveOpen(false);
                    setSelectedIds(new Set());
                },
            },
        );
    };

    if (localVendors.length === 0) {
        return (
            <SettingsEmptyState
                icon="buildingStore"
                title="No vendors configured yet"
                description={isReadOnly ? undefined : 'Add your first vendor to get started'}
                action={
                    isReadOnly ? undefined : { label: 'Add Vendor', onClick: handleOpenAdd }
                }
            />
        );
    }

    // Name + makes + the preference columns + discount, then select and actions.
    const columnCount = 2 + PREFERENCE_COLUMNS.length + 1 + (isReadOnly ? 0 : 2);

    return (
        <>
            {!isReadOnly && (
                <SettingsPageActions>
                    <PartBayButton
                        size="sm"
                        variant="solid"
                        colorScheme="partbay-blue"
                        onClick={handleOpenAdd}
                        text="Add Vendor"
                    />
                </SettingsPageActions>
            )}

            <HStack
                justify="space-between"
                align="center"
                gap={4}
                px={SETTINGS_TABLE_GUTTER}
                pb={4}
                flexShrink={0}
            >
                <InputGroup size="sm" maxW="500px">
                    <InputLeftElement>
                        <Icon as={TbSearch as React.ElementType} color="gray.400" />
                    </InputLeftElement>
                    <Input
                        placeholder="Search by vendor name..."
                        value={searchTerm}
                        onChange={(e) => handleSearchChange(e.target.value)}
                        bg="white"
                    />
                </InputGroup>
            </HStack>

            <SettingsTable
                scrollRef={tableScrollRef}
                head={
                    <>
                        {!isReadOnly && (
                            <SettingsTh w="44px">
                                <Checkbox
                                    size="sm"
                                    colorScheme="partbay-blue"
                                    isChecked={allPageSelected}
                                    isIndeterminate={!allPageSelected && somePageSelected}
                                    isDisabled={currentVendors.length === 0}
                                    onChange={(e) => handleSelectAllOnPage(e.target.checked)}
                                    aria-label="Select all vendors on this page"
                                />
                            </SettingsTh>
                        )}
                        <SettingsTh
                            color={nameSortDirection ? "#005BC4" : undefined}
                            cursor="pointer"
                            onClick={handleNameSortToggle}
                            _hover={{ color: "#005BC4" }}
                            userSelect="none"
                            transition="color 0.2s"
                        >
                            <Flex align="center" gap="6px">
                                NAME
                                <Box lineHeight={0} color={nameSortDirection ? "#005BC4" : "gray.400"}>
                                    <SortArrowIcon direction={getNameSortDirection()} size={12} />
                                </Box>
                            </Flex>
                        </SettingsTh>
                        <SettingsTh minW="130px">MAKES</SettingsTh>
                        {PREFERENCE_COLUMNS.map((column) => (
                            <SettingsTh key={column.key} textAlign="center" minW="120px">
                                {column.label}
                            </SettingsTh>
                        ))}
                        <SettingsTh minW="110px">DISCOUNT</SettingsTh>
                        {!isReadOnly && (
                            <SettingsTh textAlign="center">ACTIONS</SettingsTh>
                        )}
                    </>
                }
            >
                {currentVendors.length === 0 ? (
                    <Tr>
                        <Td colSpan={columnCount} py={10} textAlign="center">
                            <Text fontSize="sm" color="gray.500">
                                No vendors match your search
                            </Text>
                        </Td>
                    </Tr>
                ) : (
                    currentVendors.map(({ vendor, index }) => {
                        const hasDiscount = vendor.discount != null;

                        return (
                            // role="group" powers the chevron that fades in on
                            // hover for every editable cell in the row.
                            <Tr key={index} role="group" _hover={{ bg: "gray.25" }}>
                                {!isReadOnly && (
                                    <SettingsTd>
                                        <Checkbox
                                            size="sm"
                                            colorScheme="partbay-blue"
                                            isChecked={selectedIds.has(index)}
                                            onChange={(e) => handleToggleSelect(index, e.target.checked)}
                                            aria-label={`Select ${vendor.name}`}
                                        />
                                    </SettingsTd>
                                )}
                                <SettingsTd>
                                    {isReadOnly ? (
                                        <Text fontSize="sm" color="gray.800">
                                            {vendor.name}
                                        </Text>
                                    ) : (
                                        // The name is the way into the full vendor
                                        // editor; every other column edits in place.
                                        <Text
                                            as="button"
                                            fontSize="sm"
                                            textAlign="left"
                                            color="gray.800"
                                            _hover={{ color: PARTBAY_BLUE, textDecoration: 'underline' }}
                                            onClick={() => handleOpenEdit(index)}
                                        >
                                            {vendor.name}
                                        </Text>
                                    )}
                                </SettingsTd>
                                {/* Which makes this row's terms cover — the
                                    same vendor can hold several rows, one per
                                    set of makes, so this is what tells them
                                    apart in the list. */}
                                <SettingsTd>
                                    <Text
                                        fontSize="sm"
                                        color={makesLabel(vendor.makes) === '—' ? 'gray.400' : 'gray.700'}
                                        title={makesTitle(vendor.makes)}
                                        noOfLines={1}
                                    >
                                        {makesLabel(vendor.makes)}
                                    </Text>
                                </SettingsTd>
                                {PREFERENCE_COLUMNS.map((column) => (
                                    <SettingsTd key={column.key} textAlign="center">
                                        <InlineVendorPreferenceCell
                                            value={vendor[column.key]}
                                            locations={locations}
                                            ariaLabel={`Edit ${vendor.name} ${column.label.toLowerCase()} locations`}
                                            isDisabled={
                                                isReadOnly ||
                                                (!vendor.isDealer &&
                                                    (column.key === 'requiredLocations' ||
                                                        column.key === 'superPreferredLocations'))
                                            }
                                            onSave={(next) =>
                                                handlePreferenceSave(vendor, index, column.key, column.label, next)
                                            }
                                        />
                                    </SettingsTd>
                                ))}
                                <SettingsTd>
                                    <SettingsInlineNumberEdit
                                        display={
                                            <Text fontSize="sm" color={hasDiscount ? 'gray.700' : 'gray.400'}>
                                                {hasDiscount ? `${((vendor.discount ?? 0) * 100).toFixed(0)}%` : '—'}
                                            </Text>
                                        }
                                        value={hasDiscount ? Math.round((vendor.discount ?? 0) * 10000) / 100 : null}
                                        unit="%"
                                        min={0}
                                        max={100}
                                        placeholder="0"
                                        // A percentage needs a couple of characters, so
                                        // the editor stays narrow instead of matching the column.
                                        width="150px"
                                        minW="150px"
                                        ariaLabel={`Edit ${vendor.name} discount`}
                                        isDisabled={isReadOnly}
                                        onSave={(next) => handleInlineDiscountSave(vendor, index, next)}
                                    />
                                </SettingsTd>
                                {!isReadOnly && (
                                    <SettingsTd textAlign="center">
                                        <SettingsRowActions
                                            onEdit={() => handleOpenEdit(index)}
                                            onDelete={() => handleDeleteVendor(vendor, index)}
                                            editLabel="Edit vendor"
                                            deleteLabel="Delete vendor"
                                        />
                                    </SettingsTd>
                                )}
                            </Tr>
                        );
                    })
                )}
            </SettingsTable>

            <SettingsTableFooter>
                <Pagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    onPageChange={setCurrentPage}
                    py={0}
                />
            </SettingsTableFooter>

            {!isReadOnly && (
                <>
                    <BulkActionBar
                        selectedCount={selectedVendors.length}
                        actions={BULK_ACTIONS}
                        onClearSelection={() => setSelectedIds(new Set())}
                        onAction={(actionId) => {
                            if (actionId === 'discount') setIsBulkDiscountOpen(true);
                            else setIsBulkRemoveOpen(true);
                        }}
                        isLoading={isBulkSaving}
                    />

                    <VendorsBulkDiscountModal
                        isOpen={isBulkDiscountOpen}
                        selectedCount={selectedVendors.length}
                        isSaving={isBulkSaving}
                        onApply={handleBulkDiscountApply}
                        onClose={() => setIsBulkDiscountOpen(false)}
                    />

                    <AlertDialog
                        isOpen={isBulkRemoveOpen}
                        leastDestructiveRef={bulkRemoveCancelRef}
                        onClose={() => setIsBulkRemoveOpen(false)}
                        isCentered
                    >
                        <AlertDialogOverlay>
                            <AlertDialogContent>
                                <AlertDialogHeader fontSize="md">Remove vendors</AlertDialogHeader>
                                <AlertDialogCloseButton />
                                <AlertDialogBody>
                                    <Text fontSize="sm" color="gray.600">
                                        This removes {selectedVendors.length} vendor
                                        {selectedVendors.length === 1 ? '' : 's'} and their
                                        preferences and discounts. You can add them back at any
                                        time.
                                    </Text>
                                </AlertDialogBody>
                                <AlertDialogFooter gap={2}>
                                    <Button
                                        ref={bulkRemoveCancelRef}
                                        size="sm"
                                        variant="outlineGray"
                                        onClick={() => setIsBulkRemoveOpen(false)}
                                        isDisabled={isBulkSaving}
                                    >
                                        Cancel
                                    </Button>
                                    <Button
                                        size="sm"
                                        colorScheme="red"
                                        isLoading={isBulkSaving}
                                        loadingText="Removing"
                                        onClick={handleBulkRemove}
                                    >
                                        Remove
                                    </Button>
                                </AlertDialogFooter>
                            </AlertDialogContent>
                        </AlertDialogOverlay>
                    </AlertDialog>
                </>
            )}
        </>
    );
};

export default Vendors;
